import React, { useState, useEffect } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import CustomSelect from "./CustomSelect";

const RECURRING_TYPES = ["Not Applicable","Daily","Weekly","Monthly","Quarterly","Half Yearly","Yearly"];
const PRIORITIES      = ["Low", "Medium", "High"];
const DEPARTMENTS     = ["HR & ADMIN", "ACCOUNT & FINANCE", "PROJECT EXECUTION", "PURCHASE", "TENDER & MARKETING"];
const COMPANIES       = ["Aquint Infratele India Pvt Ltd", "Arkonik Fibretech India Pvt Ltd", "Aquint Global Techskill India Pvt Ltd"];

export default function CreateTaskForm({ onClose, onSuccess }) {
  const { createTask, larkUsers, uploadFile, refreshUsers } = useData();
  const { user } = useAuth();

  const [form, setForm] = useState({
    taskName:           "",
    taskDescription:    "",
    assignedUserIds:    [],   // array of Lark user IDs
    supervisorId:       "",   // single Lark user ID
    priority:           "Medium",
    startDate:          "",
    endDate:            "",
    recurringType:      "Not Applicable",
    companyName:        "Aquint Infratele India Pvt Ltd",
    assigneeDepartment: "HR & ADMIN",
    creatorDepartment:  "HR & ADMIN",
  });

  // ✅ Pre-fill Mohit Gokul Vani as default if found in users
  useEffect(() => {
    if (larkUsers?.length > 0 && form.assignedUserIds.length === 0) {
      const mohit = larkUsers.find(u => 
        u.name === "Mohit Gokul Vani" || 
        u.name === "Mohit Vani" || 
        (user?.name === "Mohit Gokul Vani" && u.id === user.userId)
      );
      if (mohit) {
        setForm(p => ({
          ...p,
          assignedUserIds: [mohit.id],
          supervisorId: mohit.id
        }));
      }
    }
  }, [larkUsers, user]);

  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [attachments, setAttachments] = useState([]);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const toggleUser = (userId) => {
    setForm(p => ({
      ...p,
      assignedUserIds: p.assignedUserIds.includes(userId)
        ? p.assignedUserIds.filter(id => id !== userId)
        : [...p.assignedUserIds, userId],
    }));
  };

  const handleSubmit = async () => {
    if (!form.taskName.trim())        return setError("Task Name is required.");
    if (!form.assignedUserIds.length) return setError("Please select at least one Assigned User.");
    if (!form.startDate)              return setError("Start Date is required.");
    if (!form.endDate)                return setError("End Date is required.");

    setError(""); setLoading(true);
    try {
      const fields = {
        "Task Name":           form.taskName,
        "Task Description":    form.taskDescription,
        "Priority":            form.priority,
        "Recurring Type":      form.recurringType,
        "Company Name":        form.companyName,
        "Assignee Department": form.assigneeDepartment,
        "Creator Department":  form.creatorDepartment,
        "Start Date":          new Date(form.startDate).getTime(),
        "End Date":            new Date(form.endDate).getTime(),
        // ✅ Assigned Users — proper Lark person format
        "Assigned Users": form.assignedUserIds.map(id => ({ id })),
      };

      // ✅ Supervisor — proper Lark person format
      if (form.supervisorId) {
        fields["Supervisor"] = [{ id: form.supervisorId }];
      }

      // ✅ Assigned By — current logged-in user's Lark ID
      if (user?.userId) {
        fields["Assigned By"] = [{ id: user.userId }];
      }

      const result = await createTask(fields);
      // Upload attachments if any
      if (attachments.length > 0) {
        const taskIdVal = result?.record?.fields?.["Task ID"] || result?.record?.record_id || "";
        for (const file of attachments) {
          try { await uploadFile(file, String(taskIdVal)); }
          catch(e) { console.warn("Attachment upload failed:", e.message); }
        }
      }
      onSuccess?.();
      onClose?.();
    } catch (e) {
      setError(e.message || "Failed to create task.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {error && <div className="alert alert-error">⚠ {error}</div>}

      <div className="form-grid">
        {/* Task Name */}
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label className="form-label">Task Name <span>*</span></label>
          <input className="form-input" placeholder="Enter task name"
            value={form.taskName} onChange={e => set("taskName", e.target.value)} />
        </div>

        {/* Description */}
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label className="form-label">Task Description</label>
          <textarea className="form-textarea" placeholder="Describe the task..."
            value={form.taskDescription} onChange={e => set("taskDescription", e.target.value)} />
        </div>

        {/* Assigned Users */}
        {/* Assigned Users */}
        <div style={{ gridColumn: "1 / -1" }}>
          <CustomSelect 
            label="Assigned Users *"
            multiple={true}
            placeholder="-- Select Assigned Users --"
            options={larkUsers}
            value={form.assignedUserIds}
            onChange={val => set("assignedUserIds", val)}
          />
        </div>

        {/* Supervisor */}
        <div>
          <CustomSelect 
            label="Supervisor"
            placeholder="-- Select Supervisor --"
            options={larkUsers}
            value={form.supervisorId}
            onChange={val => set("supervisorId", val)}
            isClearable={true}
          />
        </div>

        {/* Priority */}
        <CustomSelect 
          label="Priority"
          options={PRIORITIES}
          value={form.priority}
          onChange={val => set("priority", val)}
        />

        {/* Dates */}
        <div className="form-group">
          <label className="form-label">Start Date <span>*</span></label>
          <input type="datetime-local" className="form-input" value={form.startDate}
            onChange={e => set("startDate", e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">End Date <span>*</span></label>
          <input type="datetime-local" className="form-input" value={form.endDate}
            onChange={e => set("endDate", e.target.value)} />
        </div>

        {/* Recurring */}
        <CustomSelect 
          label="Recurring Type"
          options={RECURRING_TYPES}
          value={form.recurringType}
          onChange={val => set("recurringType", val)}
        />

        {/* Company */}
        <CustomSelect 
          label="Company"
          options={COMPANIES}
          value={form.companyName}
          onChange={val => set("companyName", val)}
        />

        {/* Departments */}
        <CustomSelect 
          label="Assignee Department"
          options={DEPARTMENTS}
          value={form.assigneeDepartment}
          onChange={val => set("assigneeDepartment", val)}
        />

        <CustomSelect 
          label="Creator Department"
          options={DEPARTMENTS}
          value={form.creatorDepartment}
          onChange={val => set("creatorDepartment", val)}
        />
      </div>

      {/* File Attachments */}
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label className="form-label">📎 Attachments</label>
          <div
            style={{
              border: "2px dashed var(--gray-300)", borderRadius: 10, padding: 16,
              textAlign: "center", background: "var(--gray-50)", cursor: "pointer", transition: "all 0.2s",
            }}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor="var(--primary)"; e.currentTarget.style.background="#EEF2FF"; }}
            onDragLeave={e => { e.currentTarget.style.borderColor="var(--gray-300)"; e.currentTarget.style.background="var(--gray-50)"; }}
            onDrop={e => {
              e.preventDefault();
              e.currentTarget.style.borderColor="var(--gray-300)";
              e.currentTarget.style.background="var(--gray-50)";
              setAttachments(p => [...p, ...Array.from(e.dataTransfer.files)]);
            }}
            onClick={() => document.getElementById("file-input-create").click()}
          >
            <div style={{ fontSize: 24, marginBottom: 4 }}>📎</div>
            <div style={{ fontSize: 13, color: "var(--gray-500)", fontWeight: 600 }}>Drop files here or click to browse</div>
            <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 2 }}>Any file type supported</div>
            <input id="file-input-create" type="file" multiple style={{ display: "none" }}
              onChange={e => setAttachments(p => [...p, ...Array.from(e.target.files)])} />
          </div>
          {attachments.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {attachments.map((file, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "white", border: "1px solid var(--gray-200)",
                  borderRadius: 8, padding: "5px 10px", fontSize: 12,
                }}>
                  <span>📄</span>
                  <span style={{ color: "var(--gray-700)", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                  <span style={{ color: "var(--gray-400)", fontSize: 10 }}>({(file.size/1024).toFixed(0)}KB)</span>
                  <button onClick={() => setAttachments(p => p.filter((_,j) => j !== i))}
                    style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 8 }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
        <button className="btn btn-primary"   onClick={handleSubmit} disabled={loading}>
          {loading ? "Creating..." : "✓ Create Task"}
        </button>
      </div>
    </div>
  );
}
